import {useEffect, useMemo, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import {useNavigate, useParams, useSearchParams} from 'react-router';
import {ChevronDown, Ghost, Mail, Plus, SquarePen} from 'lucide-react';
import {
    Alert,
    IconButton,
    Box,
    Button,
    Center,
    HStack,
    Menu,
    Portal,
    Spinner,
    Text,
    useBreakpointValue,
    useDisclosure,
    VStack,
    Span
} from '@chakra-ui/react';
import {toaster} from '../components/ui/toaster';
import {
    useCreateFolder,
    useDeleteFiles,
    useDriveCall,
    useFileMetas,
    useFolderChildren,
    useFoldersChildren,
    useRenameFile,
    useEventInfo,
    useEventInfoWithParser,
    useSaveEventInfo,
    useYearDrive,
} from '../drive/queries';
import {BIN_NAMES, BinName, DriveFile, isBinName, isFolder, isImageFile} from '../drive/types';
import {describeDriveError, getFileMediaBlob} from '../drive/driveApi';
import {BreadcrumbSegment, DriveBreadcrumb} from '../components/Breadcrumb';
import {EntryGrid, GRID_COLUMNS} from '../components/EntryGrid';
import {SelectionActionBar} from '../components/SelectionActionBar';
import {ConfirmDeleteDialog} from '../components/ConfirmDeleteDialog';
import {ConfirmArchiveDialog} from '../components/ConfirmArchiveDialog';
import {MoveFileModal, MoveFileTrailItem} from '../components/MoveFileModal';
import {RenameModal} from '../components/RenameModal';
import {CreateAlbumModal} from '../components/CreateAlbumModal';
import {EventInfoEditor} from '../components/EventInfoEditor';
import {FileContextMenu, plazmaStarAction, useContextMenu} from '../components/FileContextMenu';
import {copyFileName} from '../components/copyFileName';
import {ViewerOverlay} from './viewer/ViewerOverlay';
import {downloadBlob} from '../utils/downloadBlob';
import {usePlazmaStars} from '../hooks/usePlazmaStars';
import {useArchiveWithUndo} from '../hooks/useArchiveWithUndo';
import {canDeleteAll, DELETE_FORBIDDEN_MESSAGE} from '../domain/permissions';
import {INVALID_EVENT_INFO_MESSAGE, isCoverFile, parseEventInfoContentFile, withCover} from '../domain/eventInfo';
import {isPictureFileName, parseFolderName} from '../domain/naming';
import {
    EXPORT_MAIL_BODY,
    EXPORT_MAIL_RECIPIENT,
    exportAlbumIndex,
    exportInfoProblem,
    exportMailSubject,
    gmailComposeUrl,
} from '../domain/exportMail';
import {useAuth} from '../auth/useAuth';
import {FileFilterPopover} from '../components/FileFilterPopover';
import {NoexportBadge} from '../components/NoexportBadge';
import {SearchField, SearchToggleButton} from '../components/SearchField';
import {isSearchActive, searchByName} from '../domain/search';
import {SortToggleButton} from '../components/SortToggleButton';
import {defaultSortDirection, inSortDirection, SortDirection, toggleSortDirection} from '../domain/ordering';
import {
    applyFileFilters,
    countSelectedValues,
    FILE_FILTER_CRITERIA,
    fileFilterFacets,
    FileFilterSelection,
    isFilterActive,
    toggleFilterValue,
} from '../domain/fileFilters';

const NO_FILTER: FileFilterSelection = {};
/** The `bin` value that lists the files in the album folder itself, next to its bins (a Drive id is never this). */
const ALBUM_ROOT_BIN = 'root';
const ALBUM_ROOT_LABEL = 'Album gyökere';

/** A bin tab's picture count. */
function BinPictureCount({count, isActive}: { count: number; isActive: boolean }) {
    return <Box
        as="span"
        px={1.5}
        minW={5}
        borderRadius="full"
        fontSize="xs"
        fontWeight="medium"
        lineHeight="18px"
        textAlign="center"
        css={{fontVariantNumeric: 'tabular-nums'}}
        bg={isActive ? 'whiteAlpha.300' : 'blackAlpha.100'}
        color={isActive ? 'white' : 'gray.600'}
    >
        {count}
    </Box>;
}

export function FolderBrowserPage() {
    const params = useParams<{ year: string }>();
    const splat = useParams()['*'];
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const year = Number(params.year);

    const folderSegments = (splat ?? '').split('/').filter(Boolean);
    const isDriveRoot = folderSegments.length === 0;

    const driveQuery = useYearDrive(year);
    const driveId = driveQuery.drive?.id;
    const currentFolderId = folderSegments.length > 0 ? folderSegments[folderSegments.length - 1] : driveId;
    
    const childrenQuery = useFolderChildren(driveId, currentFolderId);
    const infoFile = childrenQuery.data?.find((f: DriveFile) => f.name === 'info.json' && !isFolder(f));
    // With the content it was read from, so setting the cover can keep the file's formatting.
    const infoQuery = useEventInfoWithParser(infoFile, parseEventInfoContentFile);
    const eventInfo = infoQuery.data?.info;
    const callDrive = useDriveCall();
    const {profile} = useAuth();

    // ---- EXPORT LETTER ----
    // The path's folder names, for the export letter (mostly cached already, for the breadcrumb).
    const pathMetas = useFileMetas(folderSegments);
    const pathNames = pathMetas.map((query) => query.data?.name);
    // Every sub-album is exported with its uppermost event folder, so the letter is about that one.
    const exportIndex = pathNames.every((name): name is string => !!name) ? exportAlbumIndex(pathNames) : -1;
    const exportAlbum = exportIndex >= 0 ? {id: folderSegments[exportIndex], name: pathNames[exportIndex] as string} : undefined;
    // Its listing and info.json, which must be complete before it can be exported (the same cache entries as when browsing it).
    const exportListing = useFolderChildren(driveId, exportAlbum?.id);
    const exportInfoFile = exportListing.data?.find((f: DriveFile) => f.name === 'info.json' && !isFolder(f));
    const exportInfoQuery = useEventInfo(exportInfoFile);

    const [selected, setSelected] = useState<Set<string>>(new Set());
    // anchorId: the fixed end of a shift-range; focusedId: the "current" item arrow keys move from.
    const [anchorId, setAnchorId] = useState<string | null>(null);
    const [focusedId, setFocusedId] = useState<string | null>(null);
    const columns = useBreakpointValue(GRID_COLUMNS) ?? 2;
    const [isDownloading, setIsDownloading] = useState(false);
    const deleteDialog = useDisclosure();
    const archiveDialog = useDisclosure();
    const moveModal = useDisclosure();
    const copyModal = useDisclosure();
    const renameModal = useDisclosure();
    const createFolderPrompt = useDisclosure();
    const createAlbumModal = useDisclosure();
    const infoEditorModal = useDisclosure();
    const contextMenu = useContextMenu();
    // The editor opens a file by id; without one it creates an info.json in the parent folder.
    const [infoEditorTarget, setInfoEditorTarget] = useState<{ parentFolderId: string; file?: DriveFile } | undefined>();

    const archiveWithUndo = useArchiveWithUndo();
    const deleteFiles = useDeleteFiles();
    const renameFile = useRenameFile();
    const saveInfo = useSaveEventInfo();
    const createFolder = useCreateFolder();

    const children = useMemo(() => childrenQuery.data ?? [], [childrenQuery.data]);
    const bins = BIN_NAMES.map((name) => ({
        name,
        folder: children.find((f: DriveFile) => isFolder(f) && f.name === name),
    }));
    // Only web/archive count as "this folder has bins" — a lone plazma folder shouldn't
    // categorize an otherwise-generic folder as an event/album.
    const hasAnyBin = bins.some((b) => b.folder && b.name !== 'plazma');

    // An album's other folders (noexport ones and the like) can be listed in place of a bin, picked by id.
    const otherFolders = hasAnyBin
        ? children
            .filter((f: DriveFile) => isFolder(f) && !isBinName(f.name))
            .map((folder) => ({folder, ...parseFolderName(folder.name)}))
            .sort((a, b) => a.displayName.localeCompare(b.displayName, 'hu'))
        : [];

    // The album folder's own files are offered only when there are some besides its info.json.
    const hasAlbumRootFiles = hasAnyBin && children.some((f: DriveFile) => !isFolder(f) && f.name !== 'info.json');

    const requestedBin = searchParams.get('bin');
    const isAlbumRootListed = hasAlbumRootFiles && requestedBin === ALBUM_ROOT_BIN;
    const activeOtherFolder = otherFolders.find((o) => o.folder.id === requestedBin);
    const activeBinEntry = hasAnyBin && !activeOtherFolder && !isAlbumRootListed
        ? (bins.find((b) => b.name === requestedBin && b.folder) ?? bins.find((b) => b.folder))
        : undefined;
    const activeBin: BinName | undefined = activeBinEntry?.name;
    // The folder listed in place of the album's contents: a bin, or one of its other folders.
    const activeBinFolder = activeOtherFolder?.folder ?? activeBinEntry?.folder;
    // ...or the album folder itself, whose files (not its folders) are then listed.
    const listedFolderId = isAlbumRootListed ? currentFolderId : activeBinFolder?.id;
    const archiveFolder = bins.find((b) => b.name === 'archive')?.folder;
    const plazmaFolder = bins.find((b) => b.name === 'plazma')?.folder;
    // Stars (copy to plazma) are offered only while browsing an album's web bin.
    const plazma = usePlazmaStars(driveId, currentFolderId, plazmaFolder, hasAnyBin && activeBin === 'web');

    const binFilesQuery = useFolderChildren(driveId, listedFolderId);
    // Every bin's listing (sharing the cache with the active one) for the picture counts on the bin tabs.
    const binListings = useFoldersChildren(driveId, hasAnyBin ? bins.map((b) => b.folder?.id) : []);

    // Filters and the search belong to the folder being browsed: in an album they carry over between its bins,
    // and they are back when returning to the folder, but other folders start without them.
    const [filterState, setFilterState] = useState<{ folderId?: string; selection: FileFilterSelection }>({selection: NO_FILTER});
    const filterSelection = hasAnyBin && filterState.folderId === currentFolderId ? filterState.selection : NO_FILTER;
    // The saved order picked per folder (not albums, whose bins keep Drive's order); others have their default.
    const [sortDirections, setSortDirections] = useState<ReadonlyMap<string, SortDirection>>(new Map());
    const sortDirection = (currentFolderId && sortDirections.get(currentFolderId)) || defaultSortDirection(isDriveRoot);
    const orderedChildren = useMemo(() => inSortDirection(children, sortDirection), [children, sortDirection]);
    // `isOpen` only matters on phones, where the field shows only while open.
    const [searchState, setSearchState] = useState<{ folderId?: string; query: string; isOpen: boolean }>({query: '', isOpen: false});
    const isSearchStateOfFolder = searchState.folderId === currentFolderId;
    const searchQuery = isSearchStateOfFolder ? searchState.query : '';
    const isSearchOpen = isSearchStateOfFolder && (searchState.isOpen || searchState.query !== '');
    const searchInputRef = useRef<HTMLInputElement>(null);

    const binFiles = useMemo(
        () => (binFilesQuery.data ?? []).filter((f: DriveFile) => !isFolder(f)),
        [binFilesQuery.data],
    );
    const searchedBinFiles = useMemo(() => searchByName(binFiles, searchQuery), [binFiles, searchQuery]);
    // The filters' counts are of the files the search leaves.
    const filterFacets = useMemo(
        () => fileFilterFacets(searchedBinFiles, FILE_FILTER_CRITERIA, filterSelection),
        [searchedBinFiles, filterSelection],
    );
    const filterActive = isFilterActive(filterSelection);
    const searchActive = isSearchActive(searchQuery);

    /** What the listing shows with the given filters and search. */
    function visibleEntriesFor(filter: FileFilterSelection, query: string): DriveFile[] {
        return hasAnyBin
            ? applyFileFilters(searchByName(binFiles, query), FILE_FILTER_CRITERIA, filter)
            : searchByName(orderedChildren, query);
    }

    // Everything acting on "the listing" (selection, shortcuts, the viewer) sees only the entries the filters
    // and the search let through.
    const listingEntries: DriveFile[] = useMemo(
        () => hasAnyBin ? applyFileFilters(searchedBinFiles, FILE_FILTER_CRITERIA, filterSelection) : searchByName(orderedChildren, searchQuery),
        [hasAnyBin, searchedBinFiles, filterSelection, orderedChildren, searchQuery],
    );
    const listingLoading = hasAnyBin ? binFilesQuery.isLoading : childrenQuery.isLoading;
    const listingParentId = hasAnyBin ? listedFolderId : currentFolderId;

    const viewerFileId = searchParams.get('file') ?? undefined;

    function selectRange(fromId: string, toId: string, ids: string[]): Set<string> {
        const fromIdx = ids.indexOf(fromId);
        const toIdx = ids.indexOf(toId);
        if (fromIdx === -1 || toIdx === -1) return new Set([toId]);
        const [start, end] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
        return new Set(ids.slice(start, end + 1));
    }

    /** Drive-style selection: plain click replaces the selection, ctrl/cmd toggles, shift selects a range. */
    function handleEntrySelect(entry: DriveFile, e: React.MouseEvent) {
        const id = entry.id;
        const ids = listingEntries.map((f) => f.id);

        if (e.shiftKey && anchorId) {
            setSelected(selectRange(anchorId, id, ids));
            setFocusedId(id);
            return;
        }
        if (e.ctrlKey || e.metaKey) {
            setSelected((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id); else next.add(id);
                return next;
            });
            setAnchorId(id);
            setFocusedId(id);
            return;
        }
        setSelected(new Set([id]));
        setAnchorId(id);
        setFocusedId(id);
    }

    /** Like Drive: right-clicking outside the selection makes that entry the selection the menu acts on. */
    function handleEntryContextMenu(entry: DriveFile, e: React.MouseEvent) {
        if (!selected.has(entry.id)) {
            setSelected(new Set([entry.id]));
            setAnchorId(entry.id);
            setFocusedId(entry.id);
        }
        contextMenu.open(e);
    }

    function toggleSort() {
        if (!currentFolderId) return;
        setSortDirections((prev) => new Map(prev).set(currentFolderId, toggleSortDirection(sortDirection)));
    }

    /** Hidden entries leave the selection, so no action can reach an entry that isn't shown. */
    function keepSelectionWithin(entries: DriveFile[]) {
        const visible = new Set(entries.map((f) => f.id));
        setSelected((prev) => new Set(Array.from(prev).filter((id) => visible.has(id))));
        if (anchorId && !visible.has(anchorId)) setAnchorId(null);
        if (focusedId && !visible.has(focusedId)) setFocusedId(null);
    }

    function setFilterSelection(selection: FileFilterSelection) {
        setFilterState({folderId: currentFolderId, selection});
        keepSelectionWithin(visibleEntriesFor(selection, searchQuery));
    }

    function setSearchQuery(query: string) {
        setSearchState({folderId: currentFolderId, query, isOpen: isSearchOpen});
        keepSelectionWithin(visibleEntriesFor(filterSelection, query));
    }

    /** Shows the field (hidden on phones until opened) and moves the focus into it. */
    function openSearch() {
        flushSync(() => setSearchState({folderId: currentFolderId, query: searchQuery, isOpen: true}));
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
    }

    /** Closing the field (on phones) also ends the search, which could no longer be seen. */
    function closeSearch() {
        setSearchState({folderId: currentFolderId, query: '', isOpen: false});
        keepSelectionWithin(visibleEntriesFor(filterSelection, ''));
    }

    function clearSelection() {
        setSelected(new Set());
        setAnchorId(null);
        setFocusedId(null);
    }

    function pathTo(ids: string[]): string {
        return `/${year}${ids.length > 0 ? '/' + ids.join('/') : ''}`;
    }

    function enterFolder(folder: DriveFile) {
        clearSelection();
        navigate(pathTo([...folderSegments, folder.id]));
    }

    function openFile(file: DriveFile) {
        if (file.name === 'info.json') {
            openInfoEditor(listingParentId, file);
            return;
        }
        const next = new URLSearchParams(searchParams);
        next.set('file', file.id);
        setSearchParams(next);
    }

    /** The selected picture the E/Space keys open: the last clicked one, or else the first one selected. */
    function selectedPictureToOpen(): DriveFile | undefined {
        const pictures = listingEntries.filter((f) => selected.has(f.id) && isImageFile(f));
        return pictures.find((f) => f.id === focusedId) ?? pictures[0];
    }

    /** Why the export letter can't be written yet (shown on hover); the button is disabled while set. */
    function exportDisabledReason(): string | undefined {
        if (!exportAlbum) return 'Az album betöltése…';
        if (!exportListing.data) return exportListing.isError ? 'Az exportálandó album nem tölthető be.' : 'Az album betöltése…';
        if (!exportInfoFile) return 'Az exportálandó albumnak még nincs info.json-ja.';
        if (exportInfoQuery.data === undefined) return exportInfoQuery.isError ? 'Az info.json nem tölthető be.' : 'Az info.json betöltése…';
        if (exportInfoQuery.data === null) return INVALID_EVENT_INFO_MESSAGE;
        return exportInfoProblem(exportInfoQuery.data);
    }

    /** Drafts the export letter in Gmail, in the signed-in account, for the user to finish and send. */
    function openExportMail() {
        if (!exportAlbum || exportDisabledReason()) return;
        window.open(gmailComposeUrl({
            account: profile?.email,
            to: EXPORT_MAIL_RECIPIENT,
            subject: exportMailSubject(exportAlbum.name),
            body: EXPORT_MAIL_BODY,
        }), '_blank', 'noopener');
    }

    function openInfoEditor(parentFolderId: string | undefined, file: DriveFile | undefined) {
        if (!parentFolderId) return;
        setInfoEditorTarget({parentFolderId, file});
        infoEditorModal.onOpen();
    }

    function closeInfoEditor() {
        setInfoEditorTarget(undefined);
        infoEditorModal.onClose();
    }

    function closeViewer() {
        const next = new URLSearchParams(searchParams);
        next.delete('file');
        setSearchParams(next);
    }

    function navigateViewerFile(fileId: string) {
        const next = new URLSearchParams(searchParams);
        next.set('file', fileId);
        setSearchParams(next);
    }

    /** Lists a bin (by name) or another folder of the album (by id). */
    function selectBin(nameOrId: string) {
        const next = new URLSearchParams(searchParams);
        next.set('bin', nameOrId);
        setSearchParams(next);
        clearSelection();
    }

    async function handleDownload() {
        // The bar's button shows it's busy; the context menu can still ask again meanwhile.
        if (isDownloading) return;
        const files = listingEntries.filter((f) => selected.has(f.id) && !isFolder(f));
        if (files.length === 0) {
            toaster.create({type: 'info', title: 'A kijelölésben nincs letölthető fájl'});
            return;
        }
        setIsDownloading(true);
        try {
            for (const file of files) {
                const blob = await callDrive((token) => getFileMediaBlob(token, file.id));
                downloadBlob(blob, file.name);
            }
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
        } finally {
            setIsDownloading(false);
        }
    }

    /** Selected web photos that also have a plazma copy — archiving/deleting/moving/renaming them warns first. */
    function selectedStarredFiles(): DriveFile[] {
        return plazma ? listingEntries.filter((f) => selected.has(f.id) && plazma.isStarred(f)) : [];
    }

    /** Drive's capabilities.canDelete for every selected entry — a mixed selection is blocked as a whole. */
    function selectionDeletable(): boolean {
        return canDeleteAll(listingEntries.filter((f) => selected.has(f.id)));
    }

    /** The selected photos' plazma copies can't be removed alongside (the user may not delete them). */
    function plazmaCopiesForbidden(): boolean {
        return !!plazma && !plazma.canRemoveCopies(selectedStarredFiles());
    }

    /** The Törlés button is disabled for such selections, but the Delete key can still get here. */
    function requestDelete() {
        if (selected.size === 0) return;
        if (!selectionDeletable()) {
            toaster.create({type: 'error', title: DELETE_FORBIDDEN_MESSAGE});
            return;
        }
        deleteDialog.onOpen();
    }

    const canMoveToArchive = hasAnyBin && activeBin === 'web' && !!archiveFolder;

    function requestMoveToArchive() {
        if (!canMoveToArchive || selected.size === 0 || archiveWithUndo.isArchiving) return;
        if (selectedStarredFiles().length > 0) archiveDialog.onOpen();
        else handleMoveToArchive(false);
    }

    async function handleMoveToArchive(alsoRemovePlazmaCopies: boolean) {
        if (!activeBinFolder || !archiveFolder) return;
        // No confirmation for a fast workflow: the success toast offers an undo instead.
        const archived = await archiveWithUndo.archive({
            files: listingEntries.filter((f) => selected.has(f.id)),
            webFolderId: activeBinFolder.id,
            archiveFolderId: archiveFolder.id,
            plazma,
            alsoRemovePlazmaCopies: alsoRemovePlazmaCopies && !plazmaCopiesForbidden(),
        });
        if (archived) clearSelection();
        archiveDialog.onClose();
    }

    async function handleDeleteSelected(alsoRemovePlazmaCopies: boolean) {
        if (!listingParentId || !selectionDeletable()) return;
        const plazmaCopies = alsoRemovePlazmaCopies && !plazmaCopiesForbidden() && plazma
            ? plazma.copiesOf(selectedStarredFiles())
            : [];
        try {
            await deleteFiles.mutateAsync({fileIds: Array.from(selected), parentId: listingParentId});
            await plazma?.removeCopies(plazmaCopies);
            toaster.create({type: 'success', title: selected.size > 1 ? `${selected.size} elem törölve` : 'Elem törölve'});
            clearSelection();
            deleteDialog.onClose();
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
            deleteDialog.onClose();
        }
    }

    async function handleRename(newName: string, alsoRenamePlazmaCopy: boolean) {
        const id = Array.from(selected)[0];
        if (!id || !listingParentId) return;
        const plazmaCopies = alsoRenamePlazmaCopy && plazma ? plazma.copiesOf(selectedStarredFiles()) : [];
        try {
            await renameFile.mutateAsync({fileId: id, newName, parentId: listingParentId});
            await plazma?.renameCopies(plazmaCopies, newName);
            toaster.create({type: 'success', title: 'Átnevezve'});
            renameModal.onClose();
            clearSelection();
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
        }
    }

    /** Why the selected picture can't be made the album cover right now, if it can't. */
    function setCoverDisabledReason(file: DriveFile): string | undefined {
        if (!infoFile) return 'Még nincs info.json — előbb add meg az album információit.';
        if (infoQuery.data === undefined) return infoQuery.isError ? 'Az info.json nem tölthető be.' : 'Az info.json betöltése…';
        if (infoQuery.data === null) return INVALID_EVENT_INFO_MESSAGE;
        if (isCoverFile(infoQuery.data.info.cover, file.name)) return 'Ez már a borítókép.';
        return undefined;
    }

    async function handleSetCover(file: DriveFile) {
        const loaded = infoQuery.data;
        if (!infoFile || !currentFolderId || !loaded) return;
        try {
            // Only the cover changes; the rest of the info and the file's formatting are kept.
            await saveInfo.mutateAsync({
                fileId: infoFile.id,
                parentFolderId: currentFolderId,
                info: withCover(loaded.info, file.name),
                beautify: loaded.content.beautified,
            });
            toaster.create({type: 'success', title: 'Borítókép beállítva'});
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
        }
    }

    async function handleCreateFolder(name: string) {
        if (!currentFolderId) return;
        try {
            await createFolder.mutateAsync({name, parentId: currentFolderId});
            toaster.create({type: 'success', title: `Létrehozva: „${parseFolderName(name).displayName}”`});
            createFolderPrompt.onClose();
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
        }
    }

    const anyModalOpen = deleteDialog.open || archiveDialog.open || moveModal.open || copyModal.open || renameModal.open
        || createFolderPrompt.open || createAlbumModal.open || infoEditorModal.open || !!viewerFileId;

    // Re-assigned every render so the single listener below always sees current state
    // (e.g. whether a selected photo was starred since it was selected).
    const onKeyDownRef = useRef<(e: KeyboardEvent) => void>(() => {});
    onKeyDownRef.current = (e: KeyboardEvent) => {
        // The context menu handles its own keys (arrows, Escape, typeahead).
        if (anyModalOpen || contextMenu.isOpen) return;
        const target = e.target as HTMLElement | null;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
        // Dialogs opened outside this page (e.g. the header's "Névjegy") hold the focus while open.
        if (target?.closest?.('[role="dialog"]')) return;

        // Plain F only: Ctrl/⌘+F stays the browser's find.
        if (e.key.toLowerCase() === 'f' && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            openSearch();
            return;
        }

        const ids = listingEntries.map((f) => f.id);
        if (ids.length === 0) return;

        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
            e.preventDefault();
            setSelected(new Set(ids));
            return;
        }

        if (e.key === 'Delete' || e.key === 'Backspace') {
            if (selected.size > 0) {
                e.preventDefault();
                requestDelete();
            }
            return;
        }

        if (e.key === 'x' && !e.ctrlKey && !e.metaKey && !e.altKey) {
            requestMoveToArchive();
            return;
        }

        // Like the context menu's star: un-stars the selected pictures if every one is starred, stars the rest otherwise.
        if (e.key.toLowerCase() === 's' && !e.ctrlKey && !e.metaKey && !e.altKey) {
            const pictures = listingEntries.filter((f) => selected.has(f.id) && isImageFile(f));
            if (plazma && pictures.length > 0) plazma.toggleAll(pictures);
            return;
        }

        if ((e.key.toLowerCase() === 'e' || e.key === ' ') && !e.ctrlKey && !e.metaKey && !e.altKey) {
            // Space on a focused button (e.g. a card's star) clicks it instead.
            if (e.key === ' ' && target?.closest?.('button, a, [role="button"]')) return;
            const picture = selectedPictureToOpen();
            if (picture) {
                e.preventDefault();
                openFile(picture);
            }
            return;
        }

        if (e.key.toLowerCase() === 'n' && !e.ctrlKey && !e.metaKey && !e.altKey) {
            const entries = listingEntries.filter((f) => selected.has(f.id));
            if (entries.length === 1) copyFileName(entries[0].name);
            return;
        }

        const arrowDeltas: Record<string, number> = {
            ArrowLeft: -1,
            ArrowRight: 1,
            ArrowUp: -columns,
            ArrowDown: columns,
        };
        const delta = arrowDeltas[e.key];
        if (delta === undefined) return;

        e.preventDefault();
        const currentId = focusedId ?? ids[0];
        const currentIdx = Math.max(0, ids.indexOf(currentId));
        const nextIdx = Math.min(ids.length - 1, Math.max(0, currentIdx + delta));
        const nextId = ids[nextIdx];

        if (e.shiftKey && anchorId) {
            setSelected(selectRange(anchorId, nextId, ids));
        } else {
            setSelected(new Set([nextId]));
            setAnchorId(nextId);
        }
        setFocusedId(nextId);
    };

    useEffect(() => {
        const listener = (e: KeyboardEvent) => onKeyDownRef.current(e);
        window.addEventListener('keydown', listener);
        return () => window.removeEventListener('keydown', listener);
    }, []);

    const currentTitle = hasAnyBin ? (eventInfo?.title || undefined) : undefined;

    const moveTrail = useMemo<MoveFileTrailItem[]>(() => {
        const currentFolderSegments = (splat ?? '').split('/').filter(Boolean);
        const trail = currentFolderSegments.map<MoveFileTrailItem>((id) => ({id}));
        if (activeBinFolder) {
            trail.push({id: activeBinFolder.id, label: parseFolderName(activeBinFolder.name).displayName});
        }
        return trail;
    }, [activeBinFolder, splat]);

    if (driveQuery.isLoading || childrenQuery.isLoading) {
        return <Center mt={10}><Spinner size="lg"/></Center>;
    }
    if (driveQuery.isError) {
        return <Center mt={10}>
            <Alert.Root status="error" maxW="md"><Alert.Indicator/><Alert.Title>{describeDriveError(driveQuery.error)}</Alert.Title></Alert.Root>
        </Center>;
    }
    if (!driveQuery.drive) {
        return <Center mt={10}><Text color="gray.500">Nem található megosztott meghajtó ehhez az évhez: {params.year}.</Text></Center>;
    }
    if (childrenQuery.isError) {
        return <Center mt={10}>
            <Alert.Root status="error" maxW="md"><Alert.Indicator/><Alert.Title>{describeDriveError(childrenQuery.error)}</Alert.Title></Alert.Root>
        </Center>;
    }

    const infoLabel = infoFile ? 'Információ szerkesztése' : 'Információ megadása';
    // What narrows the listing, worded to follow "a" (as in "… a keresésnek megfelelő").
    const narrowedBy = searchActive && filterActive ? 'keresésnek és a szűrésnek'
        : searchActive ? 'keresésnek'
            : filterActive ? 'szűrésnek' : undefined;
    const exportReason = exportDisabledReason();

    const breadcrumbPath: BreadcrumbSegment[] = [{label: 'Évek', to: '/'}];
    if (isDriveRoot) {
        breadcrumbPath.push({label: String(year)});
    } else {
        breadcrumbPath.push({label: String(year), to: pathTo([])});
        folderSegments.forEach((id, i) => {
            const isLast = i === folderSegments.length - 1;
            if (isLast && currentTitle) {
                breadcrumbPath.push({label: currentTitle});
            } else if (isLast) {
                breadcrumbPath.push({resolveId: id});
            } else {
                breadcrumbPath.push({resolveId: id, to: pathTo(folderSegments.slice(0, i + 1))});
            }
        });
    }
    const backTo = isDriveRoot ? '/' : pathTo(folderSegments.slice(0, -1));

    const selectedFiles = listingEntries.filter((f) => selected.has(f.id));
    const singleSelectedName = selectedFiles.length === 1 ? selectedFiles[0].name : '';
    const selectedStarredCount = selectedStarredFiles().length;
    // Only a single picture with valid name in an album's web bin can be made the cover
    const coverCandidate = hasAnyBin && activeBin === 'web' && 
            selectedFiles.length === 1 && isImageFile(selectedFiles[0]) && isPictureFileName(selectedFiles[0].name)
        ? selectedFiles[0]
        : undefined;
    // The menu's star acts on every selected picture (only the web bin has stars).
    const starFiles = plazma ? selectedFiles.filter(isImageFile) : [];
    // Drive can't copy folders.
    const canCopySelection = selectedFiles.length > 0 && !selectedFiles.some(isFolder);

    return <Box>
        <VStack
            gap={3}
            align="stretch"
            position="sticky"
            top="56px"
            zIndex={10}
            bg="white"
            pt={2}
            px={2}
            pb={3}
            boxShadow="sm"
        >
            <HStack justify="space-between" flexWrap="wrap">
                <DriveBreadcrumb path={breadcrumbPath} backTo={backTo}/>
                <SearchField
                    value={searchQuery}
                    onChange={setSearchQuery}
                    isOpen={isSearchOpen}
                    isActive={searchActive}
                    onClose={closeSearch}
                    inputRef={searchInputRef}
                    // Next to the buttons on larger screens; on phones a row of its own, under them.
                    order={{base: 1, md: 0}}
                    w={{base: 'full', md: '2xs'}}
                    ms={{md: 'auto'}}
                />
                <HStack>
                    <SearchToggleButton
                        isOpen={isSearchOpen}
                        isActive={searchActive}
                        onToggle={() => isSearchOpen ? closeSearch() : openSearch()}
                    />
                    {hasAnyBin &&
                        <FileFilterPopover
                            facets={filterFacets}
                            selectedCount={countSelectedValues(filterSelection)}
                            onToggle={(criterionId, value) => setFilterSelection(toggleFilterValue(filterSelection, criterionId, value))}
                            onClear={() => setFilterSelection(NO_FILTER)}
                            shownCount={listingEntries.length}
                            totalCount={searchedBinFiles.length}
                        />
                    }
                    {!hasAnyBin &&
                        <SortToggleButton direction={sortDirection} onToggle={toggleSort}/>
                    }
                    <Button size="sm" variant="outline" onClick={createFolderPrompt.onOpen}><Plus size={16}/>Új mappa</Button>
                    {/* Anywhere but inside an album (known once the listing is in). The 'isDriveRoot' is checked for performance reasons. */}
                    {(isDriveRoot || (childrenQuery.data && !hasAnyBin)) &&
                        <Button size="sm" colorPalette="blue" onClick={createAlbumModal.onOpen}><Plus size={16}/>Új album</Button>
                    }
                </HStack>
            </HStack>

            {hasAnyBin &&
                <HStack gap={2} flexWrap="wrap">
                    {bins.map(({name, folder}, i) => {
                        const isActive = name === activeBin;
                        const pictures = binListings[i]?.data?.filter(isImageFile);
                        const totalPictureCount = pictures?.length;
                        // With filters or a search on, the counts are of the pictures they let through.
                        const pictureCount = pictures && narrowedBy
                            ? applyFileFilters(searchByName(pictures, searchQuery), FILE_FILTER_CRITERIA, filterSelection).length
                            : totalPictureCount;
                        return <Button
                            key={name}
                            variant={isActive ? 'solid' : 'outline'}
                            colorPalette={isActive ? 'blue' : 'gray'}
                            size="sm"
                            textTransform="capitalize"
                            disabled={!folder}
                            onClick={() => folder && selectBin(name)}
                            title={pictureCount === undefined
                                ? undefined
                                : narrowedBy ? `${pictureCount} kép a ${narrowedBy} megfelelő (összesen ${totalPictureCount})` : `${pictureCount} kép`}
                        >
                            {name}
                            {pictureCount !== undefined && <BinPictureCount count={pictureCount} isActive={isActive}/>}
                        </Button>;
                    })}
                    {(hasAlbumRootFiles || otherFolders.length > 0) &&
                        <Menu.Root onSelect={(e) => selectBin(e.value)} positioning={{placement: 'bottom-start'}}>
                            <Menu.Trigger asChild>
                                <Button
                                    variant={activeOtherFolder || isAlbumRootListed ? 'solid' : 'outline'}
                                    colorPalette={activeOtherFolder || isAlbumRootListed ? 'blue' : 'gray'}
                                    size="sm"
                                    maxW="2xs"
                                    lineHeight="normal"
                                    title={activeOtherFolder || isAlbumRootListed ? undefined
                                        : otherFolders.length === 0 ? 'Az album gyökerének fájljai'
                                            : hasAlbumRootFiles ? 'Az album gyökere és további mappái' : 'Az album további mappái'}
                                >
                                    <Box as="span" truncate>
                                        {activeOtherFolder ? activeOtherFolder.displayName : isAlbumRootListed ? ALBUM_ROOT_LABEL : 'Egyéb mappák'}
                                    </Box>
                                    {activeOtherFolder?.noexport && <NoexportBadge shortened/>}
                                    {(activeOtherFolder || isAlbumRootListed) && !binFilesQuery.isLoading &&
                                        <BinPictureCount count={listingEntries.filter(isImageFile).length} isActive/>
                                    }
                                    <ChevronDown size={16}/>
                                </Button>
                            </Menu.Trigger>
                            <Portal>
                                <Menu.Positioner>
                                    <Menu.Content minW="200px">
                                        {hasAlbumRootFiles &&
                                            <Menu.Item value={ALBUM_ROOT_BIN} fontWeight={isAlbumRootListed ? 'semibold' : undefined}>
                                                {ALBUM_ROOT_LABEL}
                                            </Menu.Item>
                                        }
                                        {hasAlbumRootFiles && otherFolders.length > 0 && <Menu.Separator/>}
                                        {otherFolders.map(({folder, displayName, noexport}) =>
                                            <Menu.Item
                                                key={folder.id}
                                                value={folder.id}
                                                fontWeight={folder.id === activeOtherFolder?.folder.id ? 'semibold' : undefined}
                                            >
                                                {displayName}
                                                {noexport && <NoexportBadge ml="auto"/>}
                                            </Menu.Item>
                                        )}
                                    </Menu.Content>
                                </Menu.Positioner>
                            </Portal>
                        </Menu.Root>
                    }
                    <Button size="sm" variant="ghost" hideBelow="md" onClick={() => openInfoEditor(currentFolderId, infoFile)}>
                        {infoLabel}
                    </Button>
                    {/* An icon on phones, where the label would push the page wider than the screen. */}
                    <IconButton
                        size="sm"
                        variant="ghost"
                        hideFrom="md"
                        aria-label={infoLabel}
                        title={infoLabel}
                        onClick={() => openInfoEditor(currentFolderId, infoFile)}
                    >
                        <SquarePen size={18}/>
                    </IconButton>
                    <Button
                        size="sm"
                        variant="ghost"
                        disabled={!!exportReason}
                        aria-label="Exportlevél"
                        title={exportReason ?? 'Exportlevél küldése'}
                        onClick={openExportMail}
                    >
                        <HStack hideBelow="md">
                            <Mail size={16}/>Exportlevél
                        </HStack>
                        <Span hideFrom="md">
                            <Mail size={18}/>
                        </Span>
                    </Button>
                </HStack>
            }

            {selected.size > 0 &&
                <SelectionActionBar
                    count={selected.size}
                    onDownload={handleDownload}
                    isDownloading={isDownloading}
                    onRename={renameModal.onOpen}
                    onSetCover={coverCandidate && (() => handleSetCover(coverCandidate))}
                    setCoverDisabledReason={coverCandidate && setCoverDisabledReason(coverCandidate)}
                    isSettingCover={saveInfo.isPending}
                    showMoveToArchive={canMoveToArchive}
                    onMoveToArchive={requestMoveToArchive}
                    isMovingToArchive={archiveWithUndo.isArchiving}
                    onMove={moveModal.onOpen}
                    onCopy={canCopySelection ? copyModal.onOpen : undefined}
                    onDelete={requestDelete}
                    canDelete={selectionDeletable()}
                    onClear={clearSelection}
                />
            }
        </VStack>

        <Box px={2} pt={4} mb={2}>
            {listingLoading
                ? <Center mt={10}><Spinner/></Center>
                : listingEntries.length === 0 && narrowedBy
                    ? <Center mt={10}>
                        <VStack>
                            <Text color="gray.500">Egy elem sem felel meg a {narrowedBy}.</Text>
                            <HStack>
                                {searchActive &&
                                    <Button size="sm" variant="subtle" onClick={() => setSearchQuery('')}>Keresés törlése</Button>
                                }
                                {filterActive &&
                                    <Button size="sm" variant="subtle" onClick={() => setFilterSelection(NO_FILTER)}>Szűrők törlése</Button>
                                }
                            </HStack>
                        </VStack>
                    </Center>
                : listingEntries.length === 0
                    ? <Center mt={10}>
                        <VStack>
                            <Ghost size={64} strokeWidth={1} color="gray"/>
                            <Text color="gray.500">Üres.</Text>
                        </VStack>
                        </Center>
                    : <EntryGrid
                        entries={listingEntries}
                        driveId={driveId as string}
                        selected={selected}
                        onSelectEntry={handleEntrySelect}
                        onOpenFile={openFile}
                        onOpenFolder={enterFolder}
                        onEntryContextMenu={handleEntryContextMenu}
                        plazma={plazma}
                        cover={eventInfo?.cover}
                    />
            }
        </Box>

        <FileContextMenu
            menu={contextMenu}
            onDownload={selectedFiles.some((f) => !isFolder(f)) ? handleDownload : undefined}
            star={plazma && starFiles.length > 0 ? plazmaStarAction(plazma, starFiles) : undefined}
            setCover={coverCandidate && {
                disabledReason: setCoverDisabledReason(coverCandidate),
                onSelect: () => handleSetCover(coverCandidate),
            }}
            onMoveToArchive={canMoveToArchive ? requestMoveToArchive : undefined}
            onMove={moveModal.onOpen}
            onCopy={canCopySelection ? copyModal.onOpen : undefined}
            onRename={selected.size === 1 ? renameModal.onOpen : undefined}
            onDelete={requestDelete}
            deleteDisabledReason={selectionDeletable() ? undefined : DELETE_FORBIDDEN_MESSAGE}
        />
        {listingParentId &&
            <ConfirmDeleteDialog
                isOpen={deleteDialog.open}
                onClose={deleteDialog.onClose}
                onConfirm={handleDeleteSelected}
                count={selected.size}
                isDeleting={deleteFiles.isPending}
                plazmaStarredCount={selectedStarredCount}
                plazmaCopiesForbidden={plazmaCopiesForbidden()}
            />
        }
        <ConfirmArchiveDialog
            isOpen={archiveDialog.open}
            onClose={archiveDialog.onClose}
            onConfirm={handleMoveToArchive}
            count={selected.size}
            plazmaStarredCount={selectedStarredCount}
            plazmaCopiesForbidden={plazmaCopiesForbidden()}
            isArchiving={archiveWithUndo.isArchiving}
        />
        {listingParentId &&
            <MoveFileModal
                isOpen={moveModal.open}
                onClose={moveModal.onClose}
                files={selectedFiles}
                sourceFolderId={listingParentId}
                sourceDriveId={driveId as string}
                initialTrail={moveTrail}
                onDone={clearSelection}
                plazmaStarredFiles={selectedStarredFiles()}
                plazma={plazma}
            />
        }
        {listingParentId &&
            <MoveFileModal
                mode="copy"
                isOpen={copyModal.open}
                onClose={copyModal.onClose}
                files={selectedFiles}
                sourceFolderId={listingParentId}
                sourceDriveId={driveId as string}
                initialTrail={moveTrail}
            />
        }
        <RenameModal
            isOpen={renameModal.open}
            onClose={renameModal.onClose}
            currentName={singleSelectedName}
            onSubmit={handleRename}
            plazmaStarred={selectedStarredCount > 0}
            isSubmitting={renameFile.isPending}
        />
        <RenameModal
            isOpen={createFolderPrompt.open}
            onClose={createFolderPrompt.onClose}
            currentName=""
            onSubmit={handleCreateFolder}
            isSubmitting={createFolder.isPending}
            title="Új mappa"
            submitLabel="Létrehozás"
            noexportOption
        />
        {driveId && currentFolderId &&
            <CreateAlbumModal
                isOpen={createAlbumModal.open}
                onClose={createAlbumModal.onClose}
                driveId={driveId}
                parentId={currentFolderId}
                onCreated={(albumFolderId) => navigate(pathTo([...folderSegments, albumFolderId]))}
            />
        }
        {infoEditorTarget &&
            <EventInfoEditor
                isOpen={infoEditorModal.open}
                onClose={closeInfoEditor}
                fileId={infoEditorTarget.file?.id}
                fileVersion={infoEditorTarget.file?.version}
                parentFolderId={infoEditorTarget.parentFolderId}
            />
        }

        {viewerFileId &&
            <ViewerOverlay
                files={listingEntries.filter(isImageFile)}
                filesLoading={listingLoading}
                parentFolderId={listingParentId as string}
                sourceDriveId={driveId as string}
                initialTrail={moveTrail}
                showArchiveShortcut={hasAnyBin && activeBin === 'web'}
                archiveFolderId={archiveFolder?.id}
                plazma={plazma}
                currentFileId={viewerFileId}
                onClose={closeViewer}
                onNavigateFile={navigateViewerFile}
            />
        }
    </Box>;
}
